import { v4 as uuid } from 'uuid'
import { createContext, lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import './assets/styles/index.css'
import { ACCESS_TOKEN, IS_NEW_USER, LOGIN_TIME, PRIMARY_TRADING_ACCOUNT_KEY, REFRESH_TOKEN, ViewType } from './assets/constants'
import { logout } from './services/apis/auth'
import commonActions from './actions/common.actions'
import { useDispatch, useSelector } from 'react-redux'
import ROUTES from './assets/routes'
import '@mui/material/styles'
import '@mui/material'
import { Alert, Snackbar } from '@mui/material'
import { getUserProfile, getUserSubscriptionPlanFeatures } from './services/apis/user'
import { AlertData } from './components/alerts/alertMenuItem/AlertMenuItem'
import Chart from './components/chartV2/chart'
import MultiStrikeChart from './components/multiStikeChart/chart'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import AuthCheckerRoute from './components/user/acess/authCheckerRoute'
import { WebSocketRequestMessage_Subscription, WebSocketResponseMessage } from './proto/generated/websocket'
import { OrderBook, SecuritySnapshot } from './proto/generated/security'
import { Exchange, OptionGreeks } from './proto/generated/common'
import { saveUserExpressions, setSecuritiesLoaded, updateLiveData } from './reducer/security.reducer'
import { updatePrimarySecurity } from './reducer/home.reducer'
import { IWidgetDashboard, WidgetConfig } from './components/widgetDashboard/widgetDashboard.interface'
import { selectApiErrorState } from './selectors/common'
import AppWebSocketSubscriptionSync from './components/webSocketConnection/AppWebSocketSubscriptionSync'
import Dashboard from './pages/dashboard'
import 'simplebar/dist/simplebar.min.css'
import { SearchParamsProvider } from './components/providers/searchParamsProvider'
import { UpstoxOrdersPostback } from './postbacks/UpstoxOrderPostback'
import { initialState, setAllFeatures, setBrokers, setTradingAccounts, setUserDashboards, setUserDetails, setUserSubscriptionPlansData } from './reducer/user.reducer'
import { isAuthenticated } from './util/authentication'
import { isPinAuthGatePending } from './util/pinBrowserFlags'
import { applyPinSetupGateFromUser } from './components/user/acess/PinSessionUnlockGate'
import { selectPrimaryTradingAccount, selectTradingAccounts, selectUserDetails, selectUserSubscriptionPlans, selectUserSubscriptionPlansData } from './selectors/user'
import MarketDepthSync from './components/common/MarketDepthSync'
import { swapUserStore } from './store'
import { IChartApi } from 'lightweight-charts'
import AnalyticsManager from './analytics/AnalyticsManager'
import { selectQuickOrderData } from './selectors/orders'
import QuickTradeWindowV2 from './components/quickTradeWindow/QuickTradeWindowV2'
import PageWrapper from './pages/pageWrapper'
import ProtectedRoute from './components/user/acess/protectedRoute'
import CashView from './pages/home/cashView/cashView'
import OptionChain from './pages/optionChain/optionChain'
import StrategyBuilder from './pages/strategyBuilder/StrategyBuilder'
import HomePage from './pages/homePage'
import GlobalShortcutsRegister from './components/KeyboardShortcuts/GlobalShortcuts'
import ChartShortcutsRegister from './components/KeyboardShortcuts/ChartShortcutsRegister.tsx'
import AlertShortcutsRegister from './components/KeyboardShortcuts/AlertShortcutsRegister'
import SecurityNotTradableDialog from './components/quickTradeWindow/SecurityNotTradableDialog'
import { addNotifications } from './reducer/globalNotification.reducer'
import UndoRedoContextProvider from './components/UndoRedoFunctonalities/UndoRedo.context'
import { setOrderHistory } from './reducer/orders.reducer'
import { ChartAlertSnapshot } from './components/alerts/alertModal.typings'
import { setChartTypes, setExchangeTradingSessions, setIndicators, setTimeBuckets, setTradingCalendarData, updateSavedChartConfigs } from './reducer/chart.reducer'
import BrokerAuthRedirectContainer from './components/user/oauth/BrokerAuthRedirectContainer'
import { getUserDashboards, saveUserDashboard, updateUserDashboardDefault } from './services/apis/dashboard'
import { updateUserSavedLayouts } from './reducer/widgetDashboard.reducer'
import { processAndUpdateUserDashboards } from './util/widgetDashboard'
import { getDefaultLayout, getDefaultWidget, normalizePlatform } from './components/widgetDashboard/widgetDashboard.helper'
import { getDefaultChartConfig, getDefaultChartPanesConfig } from './util/chart'
import { WidgetTypes } from './components/widgetDashboard/widgetDashboard.enums'
import { selectSecuritiesLoaded, selectUserSecurityExpressions } from './selectors/security'
import { getConfiguration, getTradingCalenderData } from './services/apis/supercharts'
import ReplayManager from './components/replayManager/replayManager'
import { DashboardNamespace, DashboardShareStatus, Platform, UserDashboardProto } from './proto/generated/user-preference'
import { selectTradingCalenderData } from './selectors/chart'
import QuickWidgetsContainer from './components/quickWidgetsContainer/quickWidgetsContainer'
import { selectIsEmbedded, selectIsMobile } from './selectors/device'
import websocketActions from './actions/websocket.actions'
import RiskDisclaimerDialog from './pages/login/RiskDisclaimerDialog'
import MobileVerificationAlert from './components/mobileVerification/MobileVerificationAlert'
import PinSetupAlert from './components/pinSetup/PinSetupAlert'
import { getAllUserExpressions } from './services/apis/expression'
import { TradingAccount } from './proto/generated/trading-bridge'
import { getDisplayDate } from './util/dateTime'
import { getTradingAccounts, refetchPositionsAndOrders } from './services/apis/tradingAccount'
import { updatePositions } from './reducer/positions.reducer.ts'
import { fetchAllFeatures } from './services/apis/admin.ts'
import ProductRestrictionWrapper from './components/user/acess/ProductRestrictionWrapper.tsx'
import { FeatureCategory, FeatureName } from './components/adminUserRestrictions/Restrictions.constants.ts'
import { updateDefaultScalperSettings } from './util/scalper.ts'
import ClientVersionUpdatePrompt from './components/clientVersion/ClientVersionUpdatePrompt'
import { usePageTimeSpent } from './analytics/useTimeSpent.ts'
import { UserProvider } from './context/UserContext'
import { User } from './proto/generated/identity.ts'

const StrategyTemplate = lazy(() => import('./components/strategyTemplate/StrategyTemplate'))
const EmbeddedHome = lazy(() => import('./pages/embeddedHome'))
const AdminCache = lazy(() => import('./components/adminCache/AdminCache'))
const AdminGlobalNotification = lazy(() => import('./components/adminGlobalNotification/AdminGlobalNotification'))
const AdminUserFeatures = lazy(() => import('./components/adminUserFeatures/AdminUserFeatures'))
const AdminWatclist = lazy(() => import('./components/adminWatchlist/AdminWatclist'))
const MixpanelDashboard = lazy(() => import('./components/mixpanelDashboard/mixPanelDashboard'))
const AdminScreen = lazy(() => import('./components/adminScreens/AdminScreen'))
const AdminTradingAccount = lazy(() => import('./components/adminTradingAccounts/AdminTradingAccount.tsx'))
const AdminDematApplications = lazy(() => import('./components/adminDematApplications/AdminDematApplications.tsx'))
const AdminCorporateActions = lazy(() => import('./components/adminCorporateActions/AdminCorporateActions.tsx'))
const EmbeddedLivePayoffGraph = lazy(() => import('./pages/embeddedLivePayoffGraph/embeddedLivePayoffGraph.tsx'))
const FiiDii = lazy(() => import('./pages/fiiDiiNew'))
const AdminProtectedRoute = lazy(() => import('./components/user/acess/AdminProtectedRoute'))
const Profile = lazy(() => import('./components/user/profile/Profile'))
const Login = lazy(() => import('./pages/login'))
const Admin = lazy(() => import('./pages/adminPage/adminPage'))
const OAuthRedirectHandler = lazy(() => import('./components/user/oauth/index'))
const GoogleOAuthRedirectHandler = lazy(() => import('./components/user/oauth/GoogleOAuthRedirectHandler'))
const ZerodhaOAuthRedirectHandler = lazy(() => import('./components/user/oauth/ZerodhaOAuthRedirectHandler'))
const AngelOneOAuthRedirectHandler = lazy(() => import('./components/user/oauth/AngelOneOAuthRedirectHandler'))
const UpstoxAuthRedirectHandler = lazy(() => import('./components/user/oauth/UpstoxAuthRedirectHandler'))
const XOAuthRedirectHandler = lazy(() => import('./components/user/oauth/XOAuthRedirectHandler'))
const FyresOAuthRedirectHandler = lazy(() => import('./components/user/oauth/FyresOAuthRedirectHandler'))
const StockScreener = lazy(() => import('./components/screener/StockScreener'))
const UserStatus = lazy(() => import('./pages/userStatusPage'))
const WaitlistedUser = lazy(() => import('./pages/userStatusPage/waitlistedUser'))
const BlockedUser = lazy(() => import('./pages/userStatusPage/blockedUser'))
const MultiStrikeCharts = lazy(() => import('./pages/OpenInterestAnalysis/OIPage.tsx'))
const SeasonalsPage = lazy(() => import('./pages/seasonals/SeasonalsPage'))
const TechnicalsPage = lazy(() => import('./pages/technicals/TechnicalsPage'))
const FinancialsPage = lazy(() => import('./pages/financialData/FinancialsPage'))
const ChartPoc = lazy(() => import('./pages/lightweightPoc/chartPoc'))
const SymbolView = lazy(() => import('./pages/home/symbolView/SymbolView'))
const SpreadView = lazy(() => import('./pages/home/spreadView/SpreadView'))
const StrategyView = lazy(() => import('./pages/home/strategyView/StrategyView'))
const Simulator = lazy(() => import('./pages/simulator'))
const Scalper = lazy(() => import('./pages/scalper/scalper'))
const Settings = lazy(() => import('./pages/settings/index'))
const PnLDashboard = lazy(() => import('./pages/reports/ReportsDashboard'))
const Automation = lazy(() => import('./pages/Automation/AutomationStructure'))
const EmbeddedChart = lazy(() => import('./pages/embeddedChart/EmbeddedChart'))
const EmbeddedStrategyChart = lazy(() => import('./pages/embeddedStrategyChart/EmbeddedStrategyChart'))
const EmbeddedScalper = lazy(() => import('./pages/embeddedScalper/EmbeddedScalper'))
const EmbeddedMultistrike = lazy(() => import('./pages/embeddedMultistrike/EmbeddedMultistrike'))
const ShareholderHoldingsPage = lazy(() => import('./pages/shareholderHoldings/ShareholderHoldingsPage'))
const HeatmapPage = lazy(() => import('./pages/heatmap/HeatmapPage'))
const IndustriesOverviewPage = lazy(() => import('./pages/industries/IndustriesOverviewPage'))
const IndustryLevelPage = lazy(() => import('./pages/industries/IndustryLevelPage'))

interface AuthHandlerContextType {
    onLoggedIn: (freshToken: boolean) => void
    onLoggedOut: (dontSaveCurrUrl?: boolean) => void
}

export interface AppContextType {
    chart: Partial<Record<string, Chart>>
    oiChart: Partial<Record<string, IChartApi>>
    multiStrikeChart: Record<string, MultiStrikeChart>
    futureChart: Record<string, Chart>
    setFutureChart: (viewKey: string, chart: Chart) => void
    atmOptionChart: Record<string, Chart>
    setAtmOptonChart: (viewKey: string, chart: Chart) => void
    getChart: (viewKey: string) => Chart
    setChart: (viewKey: string, chart: Chart) => void
    removeChart: (viewKey: string) => void
    getOiChart: (viewKey: string) => IChartApi
    setOiChart: (viewKey: string, chart: IChartApi) => void
    setMultiStrikeChart: (viewKey: string, chart: MultiStrikeChart) => void
    removeMultiStrikeChart: (viewKey: string) => void
    getMultiStrikeChart: (viewKey: string) => MultiStrikeChart
    getAlertSnapshot: Partial<Record<string, () => AlertData | void>>
    setAlertSnapshotMethod: (key: string, method: () => AlertData) => void
    setChartSnapshotMethod: (key: string, method: () => ChartAlertSnapshot) => void
    getChartSnapshot: Partial<Record<string, () => ChartAlertSnapshot | void>>
    dashboard?: IWidgetDashboard
    setDashboard: (dashboard: IWidgetDashboard) => void
    unsetDashboard: () => void
    replayManager: ReplayManager
}

export const AuthHandlerContext = createContext<AuthHandlerContextType>({
    onLoggedIn: () => {},
    onLoggedOut: () => {}
})

export const AppContext = createContext<AppContextType>({
    chart: {},
    oiChart: {},
    futureChart: {},
    multiStrikeChart: {},
    atmOptionChart: {},
    getAlertSnapshot: {},
    getChartSnapshot: {},
    setAlertSnapshotMethod: () => {},
    setChartSnapshotMethod: () => {},
    setChart: () => {},
    setFutureChart: () => {},
    removeChart(viewKey) {},
    setAtmOptonChart: () => {},
    getChart: (viewKey: string) => {
        return {} as Chart
    },
    getMultiStrikeChart: (viewKey: string) => {
        return {} as MultiStrikeChart
    },
    setMultiStrikeChart: (viewKey: string, chart: MultiStrikeChart) => {},
    removeMultiStrikeChart: () => {},
    getOiChart: (viewKey: string) => {
        return {} as IChartApi
    },
    setOiChart: (viewKey: string, chart: IChartApi) => {},
    dashboard: {
        splitPane(paneId, numberOfDivisions, horizontal) {},
        deletePane(paneId) {},
        updateWidgetConfig(slotId, settings) {},
        updateSecurity(key, expressionMode, securities) {},
        broadcast(id, event, data) {},
        getActiveWidget: function (): WidgetConfig<any> | null {
            return null
        },
        openActiveWidgetSettings() {
            return false
        }
    },
    setDashboard(dashboard) {},
    unsetDashboard() {},
    replayManager: new ReplayManager({ callbacks: {} })
})
export const strategyBuilder = createContext({})

const App = () => {
    window.sessionStorage.removeItem('layoutId') // to be removed after a while, only for transition to new layouts flow;
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    usePageTimeSpent()
    const { apiError = false, errorMessage, errorType = 'error' } = useSelector(selectApiErrorState)
    const userDetails = useSelector(selectUserDetails)
    const { openDialog, type } = useSelector(selectQuickOrderData) || {}
    const authenticated = isAuthenticated()
    const isEmbedded = useSelector(selectIsEmbedded)
    const initialDataFlagRef = useRef(true)
    const userDashboardsFetched = useRef(false)
    const securitiesLoaded = useSelector(selectSecuritiesLoaded)
    const tradingCalenderData = useSelector(selectTradingCalenderData)
    const closedRightSideWidget = useRef(false)
    const isMobile = useSelector(selectIsMobile)
    const platform = isMobile ? Platform.MOBILE : Platform.DESKTOP
    const userSecurityExpressions = useSelector(selectUserSecurityExpressions)
    const userSecurityExpressionsRef = useRef(userSecurityExpressions)
    const tradingAccountsRef = useRef<TradingAccount[] | undefined>(undefined)
    const tradingAccounts: TradingAccount[] = useSelector(selectTradingAccounts)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const userSubscriptionPlansData = useSelector(selectUserSubscriptionPlansData)
    const userSubscriptionPlans = useSelector(selectUserSubscriptionPlans)

    const [showDisclaimer, setShowDisclaimer] = useState(false)
    const mobileVerified = userDetails?.currentUser?.mobileVerified ?? false

    useEffect(() => {
        if (!isEmbedded && (location.pathname === ROUTES.login || isPinAuthGatePending())) {
            setShowDisclaimer(false)
            return
        }
        if (!mobileVerified) {
            setShowDisclaimer(false)
            return
        }
        const storedRiskDiscloserCloseDate = localStorage.getItem('riskDiscloserCloseDate')
        const today = getDisplayDate(Date.now())
        if (!isEmbedded && authenticated && storedRiskDiscloserCloseDate !== today) {
            setShowDisclaimer(true)
        }
    }, [authenticated, isEmbedded, location.pathname, mobileVerified])

    const fetchTradingAccounts = useCallback(async (): Promise<TradingAccount[]> => {
        try {
            const tradingAccounts = await getTradingAccounts()
            return tradingAccounts?.tradingAccounts
        } catch (error) {
            console.error('Error fetching trading accounts', error)
            return []
        }
    }, [])

    GlobalShortcutsRegister()
    ChartShortcutsRegister()
    AlertShortcutsRegister()

    const onWebSocketMessageReceived = useCallback(
        async (data: Uint8Array) => {
            const responseData = WebSocketResponseMessage.decode(data)
            let primaryTradingAccountNew = primaryTradingAccount
            if (initialDataFlagRef.current) {
                // setting initialDataflag to false after receiving the initial data
                initialDataFlagRef.current = false
                window.dispatchEvent(new Event('tick2trade:criticalDataLoaded'))

                if (responseData.superChartConfig != null) {
                    const superChartConfig = responseData.superChartConfig
                    dispatch(setChartTypes(superChartConfig.chartTypes))
                    // dispatch(setDateRanges(superChartConfig.dateRanges))
                    dispatch(setTimeBuckets(superChartConfig.timeBuckets))
                    dispatch(setIndicators(superChartConfig.indicators))
                    dispatch(setExchangeTradingSessions(superChartConfig.exchangeTradingSessions))
                } else {
                    getConfiguration({ dispatch })
                }
                if (responseData.tradingCalendars?.tradingCalendersData) {
                    dispatch(setTradingCalendarData(responseData.tradingCalendars?.tradingCalendersData))
                } else {
                    getTradingCalenderData({ dispatch })
                }

                if ((responseData.brokers?.brokers.length ?? 0) > 0) {
                    const brokers = responseData.brokers?.brokers.filter((b) => b.enabled === true) || []
                    if (brokers) {
                        dispatch(setBrokers(brokers))
                    }
                }

                if (responseData.tradingAccounts) {
                    const tradingAccounts = responseData.tradingAccounts?.tradingAccounts
                    const sessionTradingAccountId = sessionStorage.getItem(PRIMARY_TRADING_ACCOUNT_KEY)
                    primaryTradingAccountNew = tradingAccounts?.find((t) => t.primary)
                    if (sessionTradingAccountId) {
                        const acc = tradingAccounts?.find((t) => t.accountId === sessionTradingAccountId)
                        if (acc) {
                            primaryTradingAccountNew = tradingAccounts?.find((t) => t.accountId === sessionTradingAccountId)
                        }
                    }
                    if (tradingAccounts) {
                        dispatch(setTradingAccounts(tradingAccounts))
                    }

                    if (responseData.orderUpdate && responseData.orderUpdate.tradingAccountId === primaryTradingAccountNew?.accountId) {
                        const orders = responseData.orderUpdate?.orders.reverse()
                        if (orders) {
                            dispatch(setOrderHistory({ updates: orders }))
                        }
                    }

                    let positionsLoadedFromWebsocket = false
                    if (responseData.positionUpdate && responseData.positionUpdate.tradingAccountId === primaryTradingAccountNew?.accountId) {
                        const positions = responseData.positionUpdate.positions?.position
                        if (positions) {
                            dispatch(updatePositions({ positions, overwrite: true }))
                            positionsLoadedFromWebsocket = true
                        }
                    }

                    if (primaryTradingAccountNew && !positionsLoadedFromWebsocket) {
                        void refetchPositionsAndOrders(tradingAccounts)
                    }
                }

                if (responseData.userSecurityExpressions?.securityExpressions) {
                    const userSecurityExpressions = responseData.userSecurityExpressions?.securityExpressions
                    if (userSecurityExpressions) {
                        dispatch(saveUserExpressions(userSecurityExpressions))
                    }
                }

                if ((responseData.userDashboards?.userDashboards.length ?? 0) > 0) {
                    let savedDashboards: UserDashboardProto[] = responseData.userDashboards?.userDashboards || []
                    const userDashboards = savedDashboards.filter((v) => !v.isPredefined)
                    if (!userDashboards.length) {
                        const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
                        const dashboard = getDefaultLayout()
                        const key = 'NSE:NIFTY_50'
                        const securityList = [key]
                        const widget = getDefaultWidget(newLayoutId, 1, WidgetTypes.CHART, key, false, securityList)
                        dashboard.widgets[widget.slotId] = widget
                        const chartViewKey = widget.settings.chartId
                        const chartConfig = getDefaultChartConfig()
                        const { primaryPane, seriesGroups, seriesObject } = await getDefaultChartPanesConfig(chartConfig.chartType, false, key, securityList, [], undefined, 'NIFTY')

                        chartConfig.panes = [primaryPane.id]
                        chartConfig.panesConfig = {
                            [primaryPane.id]: primaryPane
                        }
                        chartConfig.seriesGroups = seriesGroups
                        chartConfig.series = seriesObject

                        const layoutToBeSaved = {
                            panes: dashboard.panes,
                            widgets: {
                                [widget.slotId]: widget
                            },
                            syncSettings: dashboard.syncSettings,
                            charts: {
                                [chartViewKey]: chartConfig
                            },
                            drawings: {
                                [chartViewKey]: []
                            }
                        }
                        const result = await saveUserDashboard({
                            dashboard: {
                                id: newLayoutId,
                                layout: JSON.stringify(layoutToBeSaved),
                                name: 'Unnamed',
                                isDefault: true,
                                isPredefined: false,
                                shareState: DashboardShareStatus.NOT_SHARED,
                                platform: isMobile ? Platform.MOBILE : Platform.DESKTOP,
                                namespace: DashboardNamespace.CHART
                            }
                        })
                        savedDashboards = [...savedDashboards, result]
                    }
                    let { dashboards, charts, drawings, layouts } = await processAndUpdateUserDashboards(savedDashboards)
                    let filteredDashboards = dashboards.filter((d) => !d.isPredefined)
                    if (filteredDashboards.length) {
                        if (!filteredDashboards.find((d) => d.isDefault && !d.isPredefined)) {
                            const firstDashboard = filteredDashboards[0]
                            const updated = await updateUserDashboardDefault({
                                dashboard: {
                                    ...firstDashboard,
                                    isDefault: true
                                }
                            })

                            dashboards = dashboards.map((d) => (d.id === firstDashboard.id ? updated : d))
                        }
                    }

                    dispatch(setUserDashboards(dashboards))
                    dispatch(
                        updateSavedChartConfigs({
                            charts,
                            drawings
                        })
                    )
                    dispatch(updateUserSavedLayouts({ replace: false, layouts }))
                    userDashboardsFetched.current = true
                }
                // setFetchAccountHoldings(true)
            }

            const sessionTradingAccountId = sessionStorage.getItem(PRIMARY_TRADING_ACCOUNT_KEY)

            if (sessionTradingAccountId) {
                const acc = tradingAccounts?.find((t) => t.accountId === sessionTradingAccountId)
                if (acc) {
                    primaryTradingAccountNew = tradingAccounts?.find((t) => t.accountId === sessionTradingAccountId)
                }
            }

            if (responseData.positionUpdate && responseData.positionUpdate.tradingAccountId === primaryTradingAccountNew?.accountId) {
                const positions = responseData.positionUpdate.positions?.position
                if (positions?.length) {
                    dispatch(updatePositions({ positions, overwrite: !responseData.positionUpdate.isUpdate }))
                }
            }

            if (isEmbedded) return
            const snapshots: Partial<SecuritySnapshot>[] = []
            if (responseData.securitySnapshotSubscriptionResponses.length > 0) {
                responseData.securitySnapshotSubscriptionResponses.forEach((v) => {
                    v.securitySnapshots.forEach((sn) => {
                        snapshots.push({
                            security: sn.security,
                            lastTradedPrice: sn.lastTradedPrice,
                            volume: sn.volume,
                            referencedSecurity: sn.referencedSecurity
                        })
                    })
                })
            }

            if (responseData.globalNotifications.length > 0) {
                dispatch(addNotifications(responseData.globalNotifications))
            }

            let orderBooks: OrderBook[] = []
            if (responseData.orderBookSubscriptionResponses) {
                responseData.orderBookSubscriptionResponses.forEach((v) => {
                    orderBooks = [...orderBooks, ...(v.orderBooks || [])]
                })
            }

            const greeks: { securityKey: string; greeks: OptionGreeks }[] = []
            if (responseData.candleSubscriptionResponses.length > 0) {
                responseData.candleSubscriptionResponses.forEach((v) => {
                    v.securityCandles.forEach((sc) => {
                        if (sc.security && sc.candle?.optionGreeks) {
                            const securityKey = `${sc.security.exchange}:${sc.security.code}`
                            greeks.push({
                                securityKey,
                                greeks: sc.candle.optionGreeks
                            })
                        }
                    })
                })
            }

            if (snapshots.length > 0 || orderBooks.length > 0 || greeks.length > 0) {
                dispatch(
                    updateLiveData({
                        snapshots: snapshots.length ? snapshots : undefined,
                        orderBooks: orderBooks.length ? orderBooks : undefined,
                        greeks: greeks.length ? greeks : undefined
                    })
                )
            }
        },
        [isEmbedded, tradingAccounts, primaryTradingAccount]
    )

    useEffect(() => {
        if (isEmbedded) {
            getTradingCalenderData({ dispatch })
        }
    }, [])

    // useEffect(()=>{
    //     const fetchHoldings = async () => {
    //         dispatch(setHoldings([]));
    //         dispatch(setLoading());
    //         try {
    //             if(primaryTradingAccountHoldings?.accountId && primaryTradingAccountHoldings?.status == TradingAccountStatus.ACTIVE) {
    //                 const data: Holding[] = await getHoldings(primaryTradingAccountHoldings?.accountId);
    //                 dispatch(setHoldings(data));
    //             }
    //         } catch (err: any) {
    //             dispatch(setError(err.message));
    //         }
    //     };
    //     if(fetchAccountHoldings){
    //         fetchHoldings();
    //     }
    // }, [dispatch, primaryTradingAccountHoldings, fetchAccountHoldings])

    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            if ((tradingCalenderData ?? []).length === 0 && authenticated) {
                getTradingCalenderData({ dispatch })
            }
        }, 2000)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [tradingCalenderData, authenticated])

    const fetchUserDashboards = useCallback(async () => {
        let savedDashboards = await getUserDashboards()
        const platformDashboards = savedDashboards.filter((d) => !d.isPredefined && normalizePlatform(d.platform) === platform)
        if (!platformDashboards.length) {
            const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
            const dashboard = getDefaultLayout()
            const key = 'NSE:NIFTY_50'
            const securityList = [key]
            const widget = getDefaultWidget(newLayoutId, 1, WidgetTypes.CHART, key, false, securityList)
            dashboard.widgets[widget.slotId] = widget
            const chartViewKey = widget.settings.chartId
            const chartConfig = getDefaultChartConfig()
            const { primaryPane, seriesGroups, seriesObject } = await getDefaultChartPanesConfig(chartConfig.chartType, false, key, securityList, [], undefined, 'NIFTY')

            chartConfig.panes = [primaryPane.id]
            chartConfig.panesConfig = {
                [primaryPane.id]: primaryPane
            }
            chartConfig.seriesGroups = seriesGroups
            chartConfig.series = seriesObject

            const layoutToBeSaved = {
                panes: dashboard.panes,
                widgets: {
                    [widget.slotId]: widget
                },
                syncSettings: dashboard.syncSettings,
                charts: {
                    [chartViewKey]: chartConfig
                },
                drawings: {
                    [chartViewKey]: []
                }
            }
            const result = await saveUserDashboard({
                dashboard: {
                    id: newLayoutId,
                    layout: JSON.stringify(layoutToBeSaved),
                    name: 'Unnamed',
                    isDefault: true,
                    isPredefined: false,
                    shareState: DashboardShareStatus.NOT_SHARED,
                    platform: isMobile ? Platform.MOBILE : Platform.DESKTOP,
                    namespace: DashboardNamespace.CHART
                }
            })
            savedDashboards = [...savedDashboards, result]
        }
        let { dashboards, charts, drawings, layouts } = await processAndUpdateUserDashboards(savedDashboards)
        let filteredDashboards = dashboards.filter((d) => !d.isPredefined && normalizePlatform(d.platform) === platform)
        if (!filteredDashboards.find((d) => d.isDefault)) {
            const firstDashboard = filteredDashboards[0]
            if (firstDashboard) {
                const updated = await updateUserDashboardDefault({
                    dashboard: {
                        ...firstDashboard,
                        isDefault: true
                    }
                })

                dashboards = dashboards.map((d) => (d.id === firstDashboard.id ? updated : d))
            }
        }
        dispatch(setUserDashboards(dashboards))
        dispatch(
            updateSavedChartConfigs({
                charts,
                drawings
            })
        )
        dispatch(updateUserSavedLayouts({ replace: false, layouts }))
        userDashboardsFetched.current = true
    }, [platform, isMobile])

    // fetch dashboards instantly if embedded mode is set
    useEffect(() => {
        if (isEmbedded && authenticated && !userDashboardsFetched.current) {
            fetchUserDashboards()
        }
    }, [isEmbedded, authenticated, fetchUserDashboards])

    useEffect(() => {
        tradingAccountsRef.current = tradingAccounts
        const token = localStorage.getItem(ACCESS_TOKEN)
        if (token && !tradingAccounts) {
            setTimeout(async () => {
                if (!tradingAccounts && !tradingAccountsRef.current) {
                    const tradingAccounts: TradingAccount[] = await fetchTradingAccounts()
                    dispatch(setTradingAccounts(tradingAccounts))
                }
            }, 1500)
        }
    }, [dispatch, tradingAccounts])

    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            if (authenticated && securitiesLoaded && !userDashboardsFetched.current) {
                fetchUserDashboards()
            }
        }, 2000)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [authenticated, fetchUserDashboards, securitiesLoaded])

    useEffect(() => {
        const onStorageChange = (event: StorageEvent) => {
            if (event.key !== ACCESS_TOKEN && event.key !== REFRESH_TOKEN && event.key !== LOGIN_TIME) {
                return
            }
            const wasPresent = event.oldValue != null && event.oldValue !== ''
            const isPresent = event.newValue != null && event.newValue !== ''
            if (wasPresent === isPresent) {
                return
            }
            window.location.reload()
        }

        window.addEventListener('storage', onStorageChange)
        return () => window.removeEventListener('storage', onStorageChange)
    }, [])

    // added global subscription as replacement of socket heartbeat.
    useEffect(() => {
        const payload: Partial<WebSocketRequestMessage_Subscription> = {
            securitySnapshotSubscriptions: [
                {
                    tag: 'global',
                    securities: [
                        {
                            exchange: Exchange.NSE,
                            code: 'NIFTY_50'
                        }
                    ]
                }
            ]
        }
        websocketActions.setWebsocketSubscribeList(dispatch, payload)
    }, [])

    useEffect(() => {
        if (window.innerWidth <= 1280) {
            if (!closedRightSideWidget.current) {
                commonActions.dispatchRightSideWidget(dispatch, null)
                closedRightSideWidget.current = true
            }
        } else {
            closedRightSideWidget.current = false
        }
    }, [window.innerWidth])

    useEffect(() => {
        const t = setTimeout(() => window.dispatchEvent(new Event('tick2trade:criticalDataLoaded')), 4000)
        return () => clearTimeout(t)
    }, [])

    const handlePostLoginTracking = useCallback((user: User) => {
        const isNewUser = localStorage.getItem(IS_NEW_USER)
        if (isNewUser === 'false') {
            return
        }

        AnalyticsManager.logEvent('New User Signed Up', {
            email: user?.email,
            name: user?.name,
            provider: user?.provider
        })

        if (window.fbq) {
            window.fbq('track', 'New User Signed Up', {
                email: user?.email,
                name: user?.name,
                provider: user?.provider
            })
        }
        localStorage.setItem(IS_NEW_USER, 'false')
    }, [])

    useEffect(() => {
        if (authenticated) {
            if (!isEmbedded) {
                window.securityManager.loadSecurities().then(() => {
                    dispatch(setSecuritiesLoaded(true))
                })
            } else {
                dispatch(setSecuritiesLoaded(true))
            }
            getCurrentUser().then(async (response) => {
                // User may have signed out while this request was in flight (e.g. PIN setup screen).
                if (!isAuthenticated()) return
                // Prefs write must not block onboarding (e.g. 435 before PIN/mobile complete).
                let user = response
                try {
                    user = await updateDefaultScalperSettings(response)
                } catch (e) {
                    console.warn('Default scalper prefs skipped', e)
                }
                if (!isAuthenticated()) return
                handlePostLoginTracking(user)
                AnalyticsManager.setUser(user)
                dispatch(
                    setUserDetails({
                        authenticated: true,
                        currentUser: user
                    })
                )
                const gate = applyPinSetupGateFromUser(user)
                if (!isEmbedded && gate === 'ready' && isPinAuthGatePending()) {
                    // PIN unlock required before app use
                    if (location.pathname !== ROUTES.login) {
                        navigate(ROUTES.login, { replace: true })
                    }
                    return
                }
                if (!userSubscriptionPlans || !userSubscriptionPlansData) {
                    Promise.all([fetchAllFeatures(), getUserSubscriptionPlanFeatures()])
                        .then(([allFeatures, userSubscriptionPlansData]) => {
                            dispatch(setUserSubscriptionPlansData(userSubscriptionPlansData.subscriptionFeature))
                            dispatch(setAllFeatures(allFeatures.premiumFeature))
                        })
                        .catch((error) => {
                            console.error('Failed to load subscription data', error)
                        })
                }
            })
        }
        // pin setup redirect needs current path; subscription fetch uses latest store values intentionally
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, isEmbedded, handlePostLoginTracking])

    useEffect(() => {
        if (!securitiesLoaded || userSecurityExpressionsRef.current) return

        userSecurityExpressionsRef.current = userSecurityExpressions
        setTimeout(() => {
            if (!userSecurityExpressionsRef.current) {
                getAllUserExpressions({ dispatch }).catch(() => {
                    console.error('Error fetching saved expressions')
                })
            }
        }, 2000)
    }, [securitiesLoaded, userSecurityExpressions])

    const getCurrentUser = useCallback(async () => {
        const token = localStorage.getItem(ACCESS_TOKEN)
        if (token === null) {
            return Promise.reject('No access token set.')
        }
        try {
            return await getUserProfile()
        } catch (error) {
            return Promise.reject(JSON.stringify(error))
        }
    }, [])

    const onLoggedIn = useCallback(
        (freshToken: boolean) => {
            if (freshToken) {
                getCurrentUser().then(async (response) => {
                    handlePostLoginTracking(response)
                    swapUserStore(response.email)
                    AnalyticsManager.setUser(response)
                    const gate = applyPinSetupGateFromUser(response)
                    if (gate === 'ready' && isPinAuthGatePending()) {
                        navigate(ROUTES.login, { replace: true })
                        return
                    }
                    // Incomplete profile or already unlocked session: enter app
                    const prevUrl = sessionStorage.getItem('prevUrl')
                    if (prevUrl && prevUrl.length > 0) {
                        navigate(prevUrl, { replace: true })
                    } else {
                        navigate(ROUTES.base, { replace: true })
                    }
                })
            }
        },
        [getCurrentUser, navigate, handlePostLoginTracking]
    )

    const handleLogout = useCallback(() => {
        logout()
        AnalyticsManager.reset()
        userDashboardsFetched.current = false
        userSecurityExpressionsRef.current = undefined
        dispatch(setSecuritiesLoaded(false))
        dispatch(setUserDashboards([]))
        dispatch(
            updatePrimarySecurity({
                key: '',
                expressionMode: false,
                securities: [],
                underlyingSecurityCode: ''
            })
        )
        swapUserStore('anonymous')
        dispatch(
            setUserDetails({
                authenticated: false,
                currentUser: userDetails.currentUser,
                preferences: initialState.userDetails.preferences
            })
        )
        // window.location.href = ROUTES.login
        navigate(ROUTES.login, { replace: true })
    }, [dispatch, userDetails.currentUser, navigate])

    const handleApiErrorClose = useCallback(() => {
        commonActions.setApiError(dispatch, { error: false, message: errorMessage, errorType })
    }, [dispatch, errorMessage, errorType])

    const appContext: React.RefObject<AppContextType> = useRef({
        chart: {},
        oiChart: {},
        futureChart: {},
        multiStrikeChart: {},
        atmOptionChart: {},
        getAlertSnapshot: {},
        getChartSnapshot: {},
        futureSecurity: {},
        getChart: (viewKey: string) => {
            return appContext.current.chart[viewKey] as Chart
        },
        removeChart: (viewKey: string) => {
            delete appContext.current.chart[viewKey]
        },
        setAlertSnapshotMethod(viewKey, method) {
            appContext.current.getAlertSnapshot[viewKey] = method
        },
        setChartSnapshotMethod(viewKey, method) {
            appContext.current.getChartSnapshot[viewKey] = method
        },
        setChart: (viewKey: string, chart: Chart) => {
            appContext.current.chart[viewKey] = chart
            window.strategyChartManager?.setChart(viewKey, chart)
            window.chartManager?.setChart(viewKey, chart)
            window.scalperManager?.setChart(viewKey, chart)
        },
        setFutureChart: (viewKey: string, chart: Chart) => {
            appContext.current.futureChart[viewKey] = chart
        },
        getOiChart: (viewKey: string) => {
            return appContext.current.oiChart[viewKey] as IChartApi
        },
        setOiChart: (viewKey: string, chart: IChartApi) => {
            appContext.current.oiChart[viewKey] = chart
        },
        setAtmOptonChart: (viewKey: string, chart: Chart) => {
            appContext.current.atmOptionChart[viewKey] = chart
        },
        getMultiStrikeChart: (viewKey: string) => {
            return appContext.current.multiStrikeChart[viewKey]
        },
        setMultiStrikeChart: (viewKey: string, chart: MultiStrikeChart) => {
            window.multistrikeChartManager?.setChart(viewKey, chart)
            appContext.current.multiStrikeChart[viewKey] = chart
        },
        removeMultiStrikeChart: (viewKey: string) => {
            delete appContext.current.multiStrikeChart[viewKey]
        },
        dashboard: {
            splitPane(paneId, numberOfDivisions, horizontal) {},
            deletePane(paneId) {},
            updateWidgetConfig(slotId, settings) {},
            updateSecurity(key, expressionMode, securities) {},
            broadcast(id, event, data) {},
            getActiveWidget: function (): WidgetConfig<any> | null {
                return null
            },
            openActiveWidgetSettings() {
                return false
            }
        },
        setDashboard: (dashboard: IWidgetDashboard) => {
            appContext.current.dashboard = dashboard
            window.layoutManager?.setDashboard(dashboard)
        },
        unsetDashboard() {
            appContext.current.dashboard = undefined
        },
        replayManager: new ReplayManager({ callbacks: {} })
    })

    const AuthHandlerContextValue = useMemo(() => ({ onLoggedIn: onLoggedIn, onLoggedOut: handleLogout }), [handleLogout, onLoggedIn])

    return (
        <UserProvider>
            <AuthHandlerContext.Provider value={AuthHandlerContextValue}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <AppContext.Provider value={appContext.current}>
                        {!isEmbedded && (
                            <Snackbar sx={{ zIndex: 1000000000 }} open={apiError} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} autoHideDuration={5000} onClose={handleApiErrorClose}>
                                <Alert severity={errorType} onClose={handleApiErrorClose}>
                                    {errorMessage}
                                </Alert>
                            </Snackbar>
                        )}
                        <ClientVersionUpdatePrompt />
                        <SearchParamsProvider>
                            <MarketDepthSync />
                            <AppWebSocketSubscriptionSync onWebSocketMessageReceived={onWebSocketMessageReceived} initialDataFlagRef={initialDataFlagRef} />
                            <UndoRedoContextProvider appContext={appContext.current}>
                                <Dashboard>
                                    <Routes>
                                        <Route
                                            index
                                            path={ROUTES.base}
                                            element={
                                                <ProtectedRoute>
                                                    <HomePage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path={ROUTES.chart}
                                            element={
                                                <ProtectedRoute>
                                                    <PageWrapper>
                                                        <CashView view={ViewType.CHART} />
                                                    </PageWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route path={ROUTES.upstoxOrdersPostback} element={<UpstoxOrdersPostback />} />
                                        <Route
                                            path={ROUTES.symbol}
                                            element={
                                                <ProtectedRoute>
                                                    <PageWrapper>
                                                        <SymbolView />
                                                    </PageWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path={ROUTES.spread}
                                            element={
                                                <ProtectedRoute>
                                                    <ProductRestrictionWrapper
                                                        featureCategory={FeatureCategory.CHART}
                                                        featureSubCategory=""
                                                        feature={FeatureName.SECURITY_EXPRESSIONS}
                                                        revertToHomePage={true}
                                                        name="Security Expressions"
                                                    >
                                                        <PageWrapper>
                                                            <SpreadView />
                                                        </PageWrapper>
                                                    </ProductRestrictionWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path={ROUTES.strategy}
                                            element={
                                                <ProtectedRoute>
                                                    <ProductRestrictionWrapper
                                                        featureCategory={FeatureCategory.CHART}
                                                        featureSubCategory=""
                                                        feature={FeatureName.SECURITY_EXPRESSIONS}
                                                        revertToHomePage={true}
                                                        name="Security Expressions"
                                                    >
                                                        <PageWrapper>
                                                            <StrategyView />
                                                        </PageWrapper>
                                                    </ProductRestrictionWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path={ROUTES.optionChain}
                                            element={
                                                <ProtectedRoute>
                                                    <PageWrapper>
                                                        <OptionChain />
                                                    </PageWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            index
                                            path={ROUTES.strategyBuilder}
                                            element={
                                                <ProtectedRoute>
                                                    <PageWrapper>
                                                        <StrategyBuilder simulator={false} />
                                                    </PageWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path={ROUTES.scalper}
                                            element={
                                                <ProtectedRoute>
                                                    <PageWrapper>
                                                        <Scalper />
                                                    </PageWrapper>
                                                </ProtectedRoute>
                                            }
                                        />
                                    </Routes>
                                    <Suspense fallback={null}>
                                        <Routes>
                                            <Route
                                                path={ROUTES.profile}
                                                element={
                                                    <ProtectedRoute>
                                                        <Profile />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/admin"
                                                element={
                                                    <AdminProtectedRoute>
                                                        <Admin />
                                                    </AdminProtectedRoute>
                                                }
                                            >
                                                <Route index element={<Navigate to="cache" replace />} />
                                                <Route path="cache" element={<AdminCache />} />
                                                <Route path="global-notification" element={<AdminGlobalNotification />} />
                                                <Route path="watchList" element={<AdminWatclist />} />
                                                <Route path="mixpanel" element={<MixpanelDashboard />} />
                                                <Route path="community-screens" element={<AdminScreen />} />
                                                <Route path="trading-accounts" element={<AdminTradingAccount />} />
                                                <Route path="demat-applications" element={<AdminDematApplications />} />
                                                <Route path="corporate-actions" element={<AdminCorporateActions />} />
                                                <Route path="user-features" element={<AdminUserFeatures />} />
                                            </Route>
                                            <Route
                                                path={ROUTES.login}
                                                element={
                                                    <AuthCheckerRoute>
                                                        <Login />
                                                    </AuthCheckerRoute>
                                                }
                                            />
                                            <Route path={ROUTES.oauthRedirect} element={<OAuthRedirectHandler />}>
                                                <Route
                                                    path={ROUTES.googleRedirect}
                                                    element={
                                                        <AuthCheckerRoute>
                                                            <GoogleOAuthRedirectHandler />
                                                        </AuthCheckerRoute>
                                                    }
                                                />
                                                <Route
                                                    path={ROUTES.zerodhaRedirect}
                                                    element={
                                                        <BrokerAuthRedirectContainer>
                                                            <ZerodhaOAuthRedirectHandler />
                                                        </BrokerAuthRedirectContainer>
                                                    }
                                                />
                                                <Route
                                                    path={ROUTES.angelOneRedirect}
                                                    element={
                                                        <BrokerAuthRedirectContainer>
                                                            <AngelOneOAuthRedirectHandler />
                                                        </BrokerAuthRedirectContainer>
                                                    }
                                                />
                                                <Route
                                                    path={ROUTES.upstoxRedirect}
                                                    element={
                                                        <BrokerAuthRedirectContainer>
                                                            <UpstoxAuthRedirectHandler />
                                                        </BrokerAuthRedirectContainer>
                                                    }
                                                />
                                                <Route
                                                    path={ROUTES.xRedirect}
                                                    element={
                                                        <AuthCheckerRoute>
                                                            <XOAuthRedirectHandler />
                                                        </AuthCheckerRoute>
                                                    }
                                                />
                                                <Route
                                                    path={ROUTES.fyresRedirect}
                                                    element={
                                                        <BrokerAuthRedirectContainer>
                                                            <FyresOAuthRedirectHandler />
                                                        </BrokerAuthRedirectContainer>
                                                    }
                                                />
                                            </Route>

                                            <Route
                                                path={ROUTES.openInterest}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <MultiStrikeCharts />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.openInterestChange}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <MultiStrikeCharts />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.multiStrikeChart}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <MultiStrikeCharts />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.seasonals}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <SeasonalsPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.technicals}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <TechnicalsPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.financials}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <FinancialsPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.screener}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <StockScreener />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.heatmap}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <HeatmapPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.industries}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <IndustriesOverviewPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.industriesLevel}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <IndustryLevelPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.fiiDii}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <FiiDii />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.fiiDiiSummary}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <FiiDii />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.fiiDiiFuturesOptions}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <FiiDii />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.fiiDiiCashMarket}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <FiiDii />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.fiiDiiFiiHistory}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <FiiDii />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.shareholderHoldings}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <ShareholderHoldingsPage />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.simulator}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <Simulator />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.reports}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <PnLDashboard />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.automation}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <Automation />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/settings"
                                                element={<Navigate to={ROUTES.accountPersonal} replace />}
                                            />
                                            <Route
                                                path={ROUTES.account}
                                                element={<Navigate to={ROUTES.accountPersonal} replace />}
                                            />
                                            <Route
                                                path={`${ROUTES.account}/:tab`}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <Settings />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.lightweightCharts}
                                                element={
                                                    <ProtectedRoute>
                                                        <PageWrapper>
                                                            <ChartPoc />
                                                        </PageWrapper>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.userStatus}
                                                element={
                                                    <ProtectedRoute>
                                                        <UserStatus />
                                                    </ProtectedRoute>
                                                }
                                            >
                                                <Route path={ROUTES.waitlist} element={<WaitlistedUser />} />
                                                <Route path={ROUTES.blocked} element={<BlockedUser />} />
                                            </Route>
                                            <Route
                                                path={ROUTES.embeddedHome}
                                                element={
                                                    <ProtectedRoute>
                                                        <EmbeddedHome />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.embeddedChart}
                                                element={
                                                    <ProtectedRoute>
                                                        <EmbeddedChart />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.embeddedStrategyChart}
                                                element={
                                                    <ProtectedRoute>
                                                        <EmbeddedStrategyChart />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.embeddedScalper}
                                                element={
                                                    <ProtectedRoute>
                                                        <EmbeddedScalper />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.embeddedPayoff}
                                                element={
                                                    <ProtectedRoute>
                                                        <EmbeddedLivePayoffGraph />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path={ROUTES.embeddedMultistrike}
                                                element={
                                                    <ProtectedRoute>
                                                        <EmbeddedMultistrike />
                                                    </ProtectedRoute>
                                                }
                                            />
                                        </Routes>
                                        {!isEmbedded && authenticated && !isPinAuthGatePending() && <QuickWidgetsContainer />}
                                        {!isEmbedded && authenticated && !isPinAuthGatePending() && <QuickTradeWindowV2 />}
                                        {!isEmbedded && authenticated && !isPinAuthGatePending() && openDialog && type === 'notTradable' && <SecurityNotTradableDialog />}
                                        {!isEmbedded && <StrategyTemplate />}
                                        {!isEmbedded && authenticated && mobileVerified && showDisclaimer && location.pathname !== ROUTES.login && !isPinAuthGatePending() && (
                                            <RiskDisclaimerDialog
                                                open
                                                onClose={() => {
                                                    setShowDisclaimer(false)
                                                }}
                                            />
                                        )}
                                        {!isEmbedded && authenticated && !isPinAuthGatePending() && <MobileVerificationAlert />}
                                        {!isEmbedded && authenticated && !isPinAuthGatePending() && <PinSetupAlert />}
                                    </Suspense>
                                </Dashboard>
                            </UndoRedoContextProvider>
                        </SearchParamsProvider>
                    </AppContext.Provider>
                </LocalizationProvider>
            </AuthHandlerContext.Provider>
        </UserProvider>
    )
}

export default memo(App)
